import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Loader2, Save, RotateCcw } from "lucide-react";
import { useCommunitySignalScoreConfig, type SignalScoreConfig as SignalScoreConfigType } from "@/hooks/useCommunitySignalScoreConfig";

interface WeightFieldProps {
  label: string;
  description: string;
  value: number;
  onChange: (value: number) => void;
}

function WeightField({ label, description, value, onChange }: WeightFieldProps) {
  const percentage = Math.round(value * 100);
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <span className="text-sm font-mono text-muted-foreground">+{percentage}%</span>
      </div>
      <p className="text-xs text-muted-foreground">{description}</p>
      <div className="flex items-center gap-4">
        <Slider
          value={[percentage]}
          onValueChange={([v]) => onChange(v / 100)}
          max={50}
          min={0}
          step={5}
          className="flex-1"
        />
        <Input
          type="number"
          value={percentage}
          onChange={(e) => onChange(Number(e.target.value) / 100)}
          className="w-16 text-center"
          min={0}
          max={50}
        />
      </div>
    </div>
  );
}

export function SignalScoreConfigPanel() {
  const { config, isLoading, updateConfig, isUpdating } = useCommunitySignalScoreConfig();
  
  const [localConfig, setLocalConfig] = useState<Partial<SignalScoreConfigType>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Sync local state with fetched config
  useEffect(() => {
    if (config) {
      setLocalConfig({
        numeric_values_weight: config.numeric_values_weight,
        merchant_extracted_weight: config.merchant_extracted_weight,
        gift_card_keywords_weight: config.gift_card_keywords_weight,
        deal_dates_weight: config.deal_dates_weight,
        recency_bonus_weight: config.recency_bonus_weight,
        recency_hours_threshold: config.recency_hours_threshold
      });
      setHasChanges(false);
    }
  }, [config]);

  const handleChange = (field: keyof SignalScoreConfigType, value: number) => {
    setLocalConfig(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateConfig(localConfig);
    setHasChanges(false);
  };

  const handleReset = () => {
    if (config) {
      setLocalConfig({
        numeric_values_weight: config.numeric_values_weight,
        merchant_extracted_weight: config.merchant_extracted_weight,
        gift_card_keywords_weight: config.gift_card_keywords_weight,
        deal_dates_weight: config.deal_dates_weight,
        recency_bonus_weight: config.recency_bonus_weight,
        recency_hours_threshold: config.recency_hours_threshold
      });
      setHasChanges(false);
    }
  };

  // Calculate total weight
  const totalWeight = (
    (localConfig.numeric_values_weight || 0) +
    (localConfig.merchant_extracted_weight || 0) +
    (localConfig.gift_card_keywords_weight || 0) +
    (localConfig.deal_dates_weight || 0) +
    (localConfig.recency_bonus_weight || 0)
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium">Signal Score Weights</h4>
          <p className="text-xs text-muted-foreground">
            Configure how the ingestion score is calculated for each signal
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Button variant="outline" size="sm" onClick={handleReset}>
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset
            </Button>
          )}
          <Button 
            size="sm" 
            onClick={handleSave} 
            disabled={!hasChanges || isUpdating}
          >
            {isUpdating ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save
          </Button>
        </div>
      </div>

      <div className="grid gap-6">
        <WeightField
          label="Numeric Values Found"
          description="Points awarded when $X or X% values are detected in the text"
          value={localConfig.numeric_values_weight || 0}
          onChange={(v) => handleChange("numeric_values_weight", v)}
        />
        
        <WeightField
          label="Merchant Extracted"
          description="Points awarded when a known merchant is identified"
          value={localConfig.merchant_extracted_weight || 0}
          onChange={(v) => handleChange("merchant_extracted_weight", v)}
        />
        
        <WeightField
          label="Gift Card Keywords"
          description='Points awarded when "gift card", "GC", "bonus card" etc. are found'
          value={localConfig.gift_card_keywords_weight || 0}
          onChange={(v) => handleChange("gift_card_keywords_weight", v)}
        />
        
        <WeightField
          label="Deal Dates Extracted"
          description="Points awarded when validity dates are parsed from the text"
          value={localConfig.deal_dates_weight || 0}
          onChange={(v) => handleChange("deal_dates_weight", v)}
        />
        
        <WeightField
          label="Recency Bonus"
          description="Extra points for recently posted signals"
          value={localConfig.recency_bonus_weight || 0}
          onChange={(v) => handleChange("recency_bonus_weight", v)}
        />

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Recency Threshold</Label>
            <span className="text-sm font-mono text-muted-foreground">
              {localConfig.recency_hours_threshold || 48} hours
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Signals posted within this many hours receive the recency bonus
          </p>
          <div className="flex items-center gap-4">
            <Slider
              value={[localConfig.recency_hours_threshold || 48]}
              onValueChange={([v]) => handleChange("recency_hours_threshold", v)}
              max={168}
              min={12}
              step={12}
              className="flex-1"
            />
            <Input
              type="number"
              value={localConfig.recency_hours_threshold || 48}
              onChange={(e) => handleChange("recency_hours_threshold", Number(e.target.value))}
              className="w-16 text-center"
              min={12}
              max={168}
            />
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">Maximum Possible Score</span>
          <span className={`font-mono ${totalWeight > 1 ? 'text-red-500' : 'text-green-500'}`}>
            {Math.round(totalWeight * 100)}%
          </span>
        </div>
        {totalWeight > 1 && (
          <p className="text-xs text-amber-500 mt-1">
            Note: Scores are capped at 100%, but weights above this are still valid
          </p>
        )}
      </div>
    </div>
  );
}
